import type { GetOrganizationsResponse, Organization } from '@/lib/api/types/multi-orgs.types';
import { HttpError } from '@/lib/https';
import { switchOrganization } from '@/modules/auth/services/auth.service';
import type { User } from '@/types/user.type';
import { useAuthStore } from '@/state/store/auth';

export const DEFAULT_ORGANIZATION_ID = 'default';
export const DEFAULT_ORGANIZATION_NAME = 'default';

/** True when the org can be selected / switched to (not Default-disabled flags). */
export const isOrganizationEnabled = (org: {
  isEnable?: boolean;
  isDisabled?: boolean;
}): boolean => {
  if (org.isDisabled === true) return false;
  return org.isEnable !== false;
};

export const isDefaultOrganizationId = (organizationId: string | null | undefined): boolean => {
  if (!organizationId) return false;
  return organizationId.trim().toLowerCase() === DEFAULT_ORGANIZATION_ID;
};

export const isDefaultOrganization = (org: {
  itemId?: string;
  name?: string;
}): boolean => {
  if (isDefaultOrganizationId(org.itemId)) return true;
  return (org.name ?? '').trim().toLowerCase() === DEFAULT_ORGANIZATION_NAME;
};

/** Organizations shown in nav / switchers — Default is never listed. */
export const excludeDefaultOrganizations = <T extends { itemId?: string; name?: string }>(
  organizations: T[]
): T[] => organizations.filter((org) => !isDefaultOrganization(org));

/**
 * True when the user only has Default membership (or the session is on Default
 * with no non-default memberships). Those users must pick a real org.
 */
export const mustSelectNonDefaultOrganization = (
  account: User | null | undefined,
  selectedOrgId: string | null | undefined
): boolean => {
  const memberships = account?.memberships ?? [];
  const nonDefaultMemberships = memberships.filter(
    (membership) => !isDefaultOrganizationId(membership.organizationId)
  );

  if (memberships.length > 0) {
    return nonDefaultMemberships.length === 0;
  }

  return isDefaultOrganizationId(selectedOrgId);
};

/** True when the session / memberships already point at a real (non-Default) org. */
export const hasNonDefaultOrganization = (
  account: User | null | undefined,
  selectedOrgId: string | null | undefined
): boolean => !mustSelectNonDefaultOrganization(account, selectedOrgId);

export const isOrgAccessRevokedError = (error: unknown): boolean => {
  return error instanceof HttpError && (error.status === 401 || error.status === 403);
};

export const getAvailableOrganizations = (
  orgsData: GetOrganizationsResponse | undefined,
  account: User | null | undefined
): Organization[] => {
  const organizations = excludeDefaultOrganizations(
    (orgsData?.organizations ?? []).filter(isOrganizationEnabled)
  );
  const membershipOrgIds =
    account?.memberships
      ?.map((membership) => membership.organizationId)
      .filter((id) => !isDefaultOrganizationId(id)) ?? [];

  if (membershipOrgIds.length === 0) {
    return organizations;
  }

  return organizations.filter((org) => membershipOrgIds.includes(org.itemId));
};

export const isCurrentOrgUnavailable = (
  selectedOrgId: string | null,
  orgsData: GetOrganizationsResponse | undefined,
  account: User | null | undefined
): boolean => {
  if (!selectedOrgId || isDefaultOrganizationId(selectedOrgId)) {
    return false;
  }

  const availableOrganizations = getAvailableOrganizations(orgsData, account);
  if (availableOrganizations.length === 0) {
    return false;
  }

  return !availableOrganizations.some((org) => org.itemId === selectedOrgId);
};

export const shouldRecoverOrgAccess = (
  selectedOrgId: string | null,
  orgsError: unknown,
  orgsData: GetOrganizationsResponse | undefined,
  account: User | null | undefined
): boolean => {
  if (!selectedOrgId || isDefaultOrganizationId(selectedOrgId)) {
    return false;
  }

  if (orgsError && isOrgAccessRevokedError(orgsError)) {
    return true;
  }

  return isCurrentOrgUnavailable(selectedOrgId, orgsData, account);
};

export const recoverOrgAccessBySwitchingToDefault = async (): Promise<boolean> => {
  const { selectedOrgId, setTokens, refreshToken } = useAuthStore.getState();

  if (!selectedOrgId || isDefaultOrganizationId(selectedOrgId)) {
    return false;
  }

  const response = await switchOrganization(DEFAULT_ORGANIZATION_ID);

  setTokens({
    accessToken: response.access_token,
    refreshToken: (response.refresh_token || refreshToken) ?? '',
  });

  window.location.reload();
  return true;
};
